import { useEffect, useState } from 'react';
import { CircleAlert } from 'lucide-react';
import { Button } from '../../ui';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { CARD_WRAP } from './styles';

/**
 * The row a FAILED turn leaves behind (`metadata.systemError` — see lib/failed-turn.ts), drawn in place
 * of the harness's canned English apology: a compact error line in the reader's language, and "Try
 * again" when this is the newest failure and nothing has been said since. The retry re-sends the
 * message the failed turn was answering through the chat's ordinary send path, so it is exactly the
 * turn the reader would have got by retyping it.
 *
 * `line` replaces the default copy when the failure has a more specific reason — a send the server refused
 * for carrying more than 20 attachments says so (`chat.attachments.limit`).
 */
export function FailedTurnRow({ messageId, onRetry, line }: { messageId?: string; onRetry?: () => void; line?: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  // Latched by a click so a second click before the re-render (send() reads `streaming` from its closure,
  // which is still false in the same tick) cannot send twice. Re-armed when the button goes away.
  const [tapped, setTapped] = useState(false);
  useEffect(() => {
    if (!onRetry) setTapped(false);
  }, [onRetry]);
  return (
    <div style={CARD_WRAP} data-msg-id={messageId} data-testid="chat-failed-turn">
      <div role="status" style={LINE}>
        <CircleAlert size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden style={ICON} />
        <span dir="auto">{line ?? t('chat.turnFailed.message')}</span>
      </div>
      {onRetry && (
        <Button
          variant="text"
          size="small"
          disabled={tapped}
          onClick={() => {
            if (tapped) return;
            setTapped(true);
            logPageClickBi({ page_name: 'chats', button_name: 'retry_failed_turn' });
            onRetry();
          }}
        >
          {t('chat.turnFailed.retry')}
        </Button>
      )}
    </div>
  );
}

const LINE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  fontSize: '0.9375rem',
  lineHeight: '1.375rem',
  color: 'var(--ds-text-destructive)',
};

// Centre the 16px glyph on the first 22px line box, so a wrapped line keeps the icon at the top.
const ICON: React.CSSProperties = { flexShrink: 0, marginTop: 3 };
